import { t } from 'i18next';
import CustomRichTextEditor from '../Marketing/CustomRichTextEditor';
import {
    RadixAccordion,
    RadixAccordionGroup,
    RadixButton,
    RadixCheckbox,
    RadixDialog,
    RadixInput,
    RadixMultiSelect,
    RadixSelect,
    RadixSwitch,
    RadixTextarea,
    RadixFileUpload,
} from '../radix';
import { useMemo, useRef, useState } from 'react';
import type { CustomRichTextEditorHandle } from '../Marketing/CustomRichTextEditor';
import {
    defaultAdvanceReminderEmail,
    AdvanceReminderKeywords,
} from '@/scenes/Services/AdvanceReminder/AdvanceReminderDefaultEmail';
import {
    ReminderFormDataType,
    ReminderTriggerType,
    SelectedMsgTypeOptions,
} from '@/scenes/Services/AdvanceReminder/adv-reminder.types';
import { buildTriggerTimeOptions, buildExcludeBookingStatusOptions, DEFAULT_TRIGGER_TIME } from '@/scenes/Services/AdvanceReminder/reminderTriggerUtils';
import { useSelector } from 'react-redux';
import generatePreviewHtml from '@/utils/PreviewHtml';
import { toast } from 'sonner';
import { PostApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem } from '@/shared/api/models/postApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem';
import { createFilesApi, deleteFileApi } from '@/utils/Api/File';

const MAX_ATTACHMENT_FILES = 5;
const MAX_ATTACHMENT_TOTAL_SIZE_MB = 15;
const MAX_ATTACHMENT_TOTAL_SIZE_BYTES = MAX_ATTACHMENT_TOTAL_SIZE_MB * 1024 * 1024;

const ReminderForm = ({
    formik,
    durationOptions,
    services,
    isPreview,
    setIsPreview,
    onAutoSaveReminderTemplate,
}: ReminderFormDataType) => {
    const isDayOfBooking = formik?.values?.reminderTriggerType === 'DAY_OF_BOOKING';
    const isAfterBooking = formik?.values?.reminderTriggerType === 'AFTER_BOOKING';
    const triggerTimeOptions = useMemo(() => buildTriggerTimeOptions(), []);
    const excludeBookingStatusOptions = useMemo(() => buildExcludeBookingStatusOptions(), []);
    const editorRef = useRef<CustomRichTextEditorHandle | null>(null);
    const smsEditorRef = useRef<HTMLTextAreaElement | null>(null);
    const setting = useSelector((state: any) => state?.settings?.data?.profile);
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] =
        useState<PostApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem | null>(null);
    const [isDeleteAttachmentModalOpen, setIsDeleteAttachmentModalOpen] = useState(false);

    const attachments = formik?.values?.attachments || [];

    const updateServices = (newSet: Set<string>) => {
        formik.setFieldValue('services', Array.from(newSet));
    };

    const toggleService = (id: string, checked: boolean) => {
        const updated = new Set(formik?.values?.services);
        checked ? updated.add(id) : updated.delete(id);
        updateServices(updated as any);
    };

    const toggleGroup = (ids: string[], checked: boolean) => {
        const updated = new Set(formik?.values?.services);
        ids.forEach((id) => {
            checked ? updated.add(id) : updated.delete(id);
        });
        updateServices(updated as any);
    };

    const uploadAttachments = async (files: File[]) => {
        const uploadedFiles = await createFilesApi({ files });
        return uploadedFiles.map((item) => ({
            id: item.id,
            attachment_name: item.fileName,
            attachment: item.filePath,
            created_at: item.createdAt,
            updated_at: item.updatedAt,
            deleted_at: item.deletedAt,
        }));
    };

    const handleAttachmentUpload = async (selectedFileList: FileList | null) => {
        const selectedFiles = Array.from(selectedFileList || []);
        if (!selectedFiles.length) return;

        if (attachments.length + selectedFiles.length > MAX_ATTACHMENT_FILES) {
            toast.error(t('Services.MaxAttachmentCount', { count: MAX_ATTACHMENT_FILES }));
            return;
        }

        const selectedSize = selectedFiles.reduce((total, file) => total + file.size, 0);
        if (selectedSize > MAX_ATTACHMENT_TOTAL_SIZE_BYTES) {
            toast.error(t('Services.MaxAttachmentBatchSize', { size: MAX_ATTACHMENT_TOTAL_SIZE_MB }));
            return;
        }

        setIsUploadingAttachment(true);
        const loadingToastId = toast.loading(t('Services.AttachmentUploading'));

        try {
            const uploadedAttachments = await uploadAttachments(selectedFiles);
            if (!uploadedAttachments.length) {
                throw new Error('No attachments returned');
            }

            const nextValues = {
                ...formik.values,
                attachments: [...attachments, ...uploadedAttachments],
            };
            formik.setFieldValue('attachments', nextValues.attachments);
            toast.success(t('Services.AttachmentUploadedSuccessfully'), { id: loadingToastId });
            await new Promise((resolve) => setTimeout(resolve, 500));
            await triggerAutoSave(nextValues);
        } catch (error) {
            toast.error(t('Services.AttachmentUploadFailed'), { id: loadingToastId });
        } finally {
            setIsUploadingAttachment(false);
        }
    };

    const confirmDeleteAttachment = (attachment: PostApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem) => {
        setAttachmentToDelete(attachment);
        setIsDeleteAttachmentModalOpen(true);
    };

    const handleDeleteAttachment = async () => {
        if (!attachmentToDelete?.id || isDeletingAttachment) return;

        setIsDeletingAttachment(true);
        const nextValues = {
            ...formik.values,
            attachments: attachments.filter((attachment) => attachment.id !== attachmentToDelete.id),
        };
        try {
            const deleteRequest = deleteFileApi({ id: attachmentToDelete.id }).catch((error) => {
                const statusCode = (error as { status?: number })?.status;
                if (statusCode === 404) return { alreadyDeleted: true };
                throw error;
            });

            await toast.promise(deleteRequest, {
                loading: t('Services.AttachmentDeleting'),
                success: t('Services.AttachmentDeletedSuccessfully'),
                error: t('Services.AttachmentDeleteFailed'),
            });

            formik.setFieldValue('attachments', nextValues.attachments);
            await new Promise((resolve) => setTimeout(resolve, 500));
            await triggerAutoSave(nextValues);
            setIsDeleteAttachmentModalOpen(false);
            setAttachmentToDelete(null);
        } catch (error) {
            // error toast handled by toast.promise
        } finally {
            setIsDeletingAttachment(false);
        }
    };

    const getAttachmentDisplayName = (attachment: PostApiServiceAdvancedReminderTemplatesBodyAttachmentsAnyOfItem) => {
        if (attachment?.attachment_name) return attachment.attachment_name;
        const filePath = attachment?.attachment || '';
        const segments = filePath.split('/');
        return segments[segments.length - 1] || filePath;
    };

    const triggerAutoSave = async (nextValues: typeof formik.values) => {
        if (!nextValues?.reminderTemplateId) return;

        const autoSaveToastId = toast.loading(t('Services.AutoSavingAdvancedReminder'));
        try {
            const isSaved = await onAutoSaveReminderTemplate(nextValues);
            if (isSaved) {
                toast.success(t('Services.AdvancedReminderAutoSaved'), { id: autoSaveToastId });
            } else {
                toast.error(t('Services.AdvancedReminderAutoSaveFailed'), { id: autoSaveToastId });
            }
        } catch (error) {
            toast.error(t('Services.AdvancedReminderAutoSaveFailed'), { id: autoSaveToastId });
        }
    };

    return (
        <div className="w-full h-full min-h-0 grid grid-cols-1 gap-4 md:grid-cols-2  md:gap-2 md:overflow-hidden">
            <div
                role="alert"
                aria-live="polite"
                className="w-full md:col-span-2 rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800"
            >
                {t('Services.TemplateSelectionInfo')}
            </div>
            {/* LEFT SIDE */}
            <div className="w-full space-y-4 md:h-full md:overflow-y-auto md:pr-2 md:min-h-0 custom-scroll">
                <div>
                    <h4 className="mt-0 mb-1 font-normal">{t('Services.TemplateName')}</h4>
                    <RadixInput
                        name="reminderTemplateName"
                        placeholder={t('Services.EnterTemplateName')}
                        value={formik?.values?.reminderTemplateName}
                        onChange={formik?.handleChange}
                        onBlur={formik?.handleBlur}
                    />
                    {formik?.touched?.reminderTemplateName && formik?.errors?.reminderTemplateName && (
                        <p className="text-red-500 text-sm mt-1">{formik?.errors?.reminderTemplateName}</p>
                    )}
                </div>

                <div className="flex flex-col lg:flex-row lg:items-start gap-2 lg:gap-4 w-full">
                    <div className="w-auto">
                        <h4 className="mb-1 font-normal">{t('Services.MsgType')}</h4>
                        <RadixMultiSelect
                            className="w-full md:w-fit"
                            options={[
                                { label: t('Calendar.SMS'), value: 'SMS' },
                                { label: t('Common.Email'), value: 'EMAIL' },
                            ]}
                            selectedValues={
                                formik?.values?.reminderTemplateType === 'EMAIL_AND_SMS'
                                    ? new Set(['SMS', 'EMAIL'])
                                    : new Set([formik?.values?.reminderTemplateType])
                            }
                            textToDisplayWithCount={t('Services.SMSEmail')}
                            hideCount={true}
                            onSelectionChange={(selectedValues: Set<string>) => {
                                const values = Array.from(selectedValues);

                                let newType: SelectedMsgTypeOptions = 'SMS';

                                if (values.length === 2) {
                                    newType = 'EMAIL_AND_SMS';
                                } else if (values.includes('SMS')) {
                                    newType = 'SMS';
                                } else if (values.includes('EMAIL')) {
                                    newType = 'EMAIL';
                                }

                                formik.setFieldValue('reminderTemplateType', newType);
                                if (newType === 'SMS') {
                                    formik.setFieldValue('attachments', []);
                                }
                            }}
                        />
                    </div>

                    <div className="w-auto">
                        <h4 className="mb-1 font-normal">{t('Services.ReminderTriggerType')}</h4>
                        <RadixSelect
                            className="w-full md:w-fit"
                            options={[
                                {
                                    label: t('Services.ReminderTriggerBeforeBooking'),
                                    value: 'BEFORE_BOOKING',
                                },
                                {
                                    label: t('Services.ReminderTriggerDayOfBooking'),
                                    value: 'DAY_OF_BOOKING',
                                },
                                {
                                    label: t('Services.ReminderTriggerAfterBooking'),
                                    value: 'AFTER_BOOKING',
                                },
                            ]}
                            value={formik?.values?.reminderTriggerType}
                            placeholder={t('Services.ReminderTriggerType')}
                            onValueChange={(val) => {
                                const nextType = val as ReminderTriggerType;
                                formik.setFieldValue('reminderTriggerType', nextType);
                                if (nextType === 'DAY_OF_BOOKING' && !formik.values.triggerTime) {
                                    formik.setFieldValue('triggerTime', DEFAULT_TRIGGER_TIME);
                                }
                            }}
                        />
                    </div>

                    <div className="w-auto">
                        {isDayOfBooking ? (
                            <>
                                <h4 className="mb-1 font-normal">{t('Services.TriggerTime')}</h4>
                                <RadixSelect
                                    className="w-full md:w-auto"
                                    options={triggerTimeOptions}
                                    value={formik?.values?.triggerTime || DEFAULT_TRIGGER_TIME}
                                    placeholder={t('Services.TriggerTime')}
                                    onValueChange={(val) => formik.setFieldValue('triggerTime', val)}
                                />
                                {formik?.touched?.triggerTime && formik?.errors?.triggerTime && (
                                    <p className="text-red-500 text-sm mt-1">{formik.errors.triggerTime}</p>
                                )}
                            </>
                        ) : (
                            <>
                                <h4 className="mb-1 font-normal">
                                    {isAfterBooking
                                        ? t('Services.ReminderAfterBookingDuration')
                                        : t('Services.SelectDuration')}
                                </h4>
                                <RadixSelect
                                    className="w-full md:w-auto"
                                    options={durationOptions}
                                    value={
                                        typeof formik?.values?.reminderMinutes === 'number'
                                            ? `${formik.values.reminderMinutes}min`
                                            : formik?.values?.reminderMinutes
                                    }
                                    placeholder={t('Services.SelectDuration')}
                                    onValueChange={(val) =>
                                        formik?.setFieldValue('reminderMinutes', parseInt(val.replace('min', ''), 10))
                                    }
                                />
                            </>
                        )}
                    </div>
                </div>

                {isAfterBooking && (
                    <div>
                        <h4 className="mb-1 font-normal">{t('Services.ExcludeBookingStatuses')}</h4>
                        <p className="text-sm text-gray-500 m-0">{t('Services.ExcludeBookingStatusesHint')}</p>
                        <RadixMultiSelect
                            className="w-full md:w-fit"
                            placeholder={t('Services.ExcludeBookingStatuses')}
                            options={excludeBookingStatusOptions}
                            selectedValues={new Set(formik?.values?.excludeBookingStatuses || [])}
                            textToDisplayWithCount={t('Services.ExcludeBookingStatuses')}
                            onSelectionChange={(selectedValues: Set<string>) => {
                                formik.setFieldValue('excludeBookingStatuses', Array.from(selectedValues));
                            }}
                        />
                    </div>
                )}

                <div className="flex items-center gap-8">
                    <div>
                        <h4 className="mb-1 font-normal">{t('Services.IsActive')}</h4>
                        <RadixSwitch
                            checked={formik?.values?.isActive}
                            onChange={(checked) => formik?.setFieldValue('isActive', checked)}
                            name="isActive"
                        />
                    </div>
                </div>

                {(formik?.values?.reminderTemplateType === 'SMS' ||
                    formik?.values?.reminderTemplateType === 'EMAIL_AND_SMS') && (
                    <div>
                        <h4 className="mb-1 font-normal">{t('Services.SmSContent')}</h4>
                        <RadixTextarea
                            ref={smsEditorRef}
                            name="smsContent"
                            value={
                                formik?.values?.reminderTemplateType === 'SMS' ||
                                formik?.values?.reminderTemplateType === 'EMAIL_AND_SMS'
                                    ? formik?.values?.smsContent
                                    : ''
                            }
                            onChange={formik?.handleChange}
                            onBlur={formik?.handleBlur}
                            rows={12}
                        />
                        <div className="flex flex-wrap gap-x-2 gap-y-3 mt-3">
                            {AdvanceReminderKeywords?.map((keywords: string, i: number) => (
                                <RadixButton
                                    key={i}
                                    onClick={() => {
                                        const textarea = smsEditorRef.current;
                                        if (!textarea) return;

                                        const start = textarea.selectionStart;
                                        const end = textarea.selectionEnd;
                                        const text = formik.values.smsContent || '';

                                        const newText =
                                            text.substring(0, start) + `\${${keywords}} ` + text.substring(end);

                                        formik.setFieldValue('smsContent', newText);
                                        textarea.focus();
                                    }}
                                    variant="outline"
                                    size="xs"
                                    className="!bg-[#fff4e9] !border-none !py-1 !px-2 !h-auto m-0 whitespace-nowrap"
                                >
                                    {`{${keywords}}`}
                                </RadixButton>
                            ))}
                        </div>
                        {formik.touched.smsContent && formik.errors.smsContent && (
                            <p className="text-red-500 text-sm mt-1">{formik.errors.smsContent}</p>
                        )}
                    </div>
                )}

                {(formik?.values?.reminderTemplateType === 'EMAIL' ||
                    formik?.values?.reminderTemplateType === 'EMAIL_AND_SMS') && (
                    <div>
                        <h4 className="mb-1 font-normal">{t('Services.EmailContent')}</h4>
                        <div className="border">
                            <RadixInput
                                value={formik.values.emailSubject || ''}
                                placeholder={t('Services.EmailHeaderPlaceholder')}
                                className="w-full h-10 border-b rounded-none px-0 rounded-t-md py-1"
                                name="emailSubject"
                                onChange={(e) => formik.setFieldValue('emailSubject', e?.target?.value)}
                                onBlur={formik.handleBlur}
                            />

                            <CustomRichTextEditor
                                ref={editorRef}
                                key={formik?.values?.reminderTemplateId}
                                value={generatePreviewHtml({ formik, defaultAdvanceReminderEmail, setting })}
                                onChange={(val: string) => formik.setFieldValue('emailContent', val)}
                                className="w-full px-0 border rounded-none rounded-b-md"
                            />
                            <div className="flex flex-wrap gap-x-2 gap-y-3 mt-3">
                                {AdvanceReminderKeywords?.map((keywords: string, i: number) => (
                                    <RadixButton
                                        key={i}
                                        onClick={() => editorRef.current?.insertTextAtCursor(`\${${keywords}} `)}
                                        variant="outline"
                                        size="xs"
                                        className="!bg-[#fff4e9] !border-none !py-1 !px-2 !h-auto m-0 whitespace-nowrap"
                                    >
                                        {`{${keywords}}`}
                                    </RadixButton>
                                ))}
                            </div>
                            {formik?.touched?.emailContent && formik?.errors?.emailContent && (
                                <p className="text-red-500 text-sm mt-1">{formik?.errors?.emailContent}</p>
                            )}
                            {formik?.touched?.emailSubject && formik?.errors?.emailSubject && (
                                <p className="text-red-500 text-sm mt-1">{formik?.errors?.emailSubject}</p>
                            )}
                        </div>
                    </div>
                )}

                {(formik?.values?.reminderTemplateType === 'EMAIL' ||
                    formik?.values?.reminderTemplateType === 'EMAIL_AND_SMS') && (
                    <div>
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <h4 className="m-0 font-normal">{t('Calendar.UploadedFiles')}</h4>
                        </div>

                        <RadixFileUpload
                            onFilesSelected={handleAttachmentUpload}
                            multiple
                            disabled={isUploadingAttachment}
                            buttonText={isUploadingAttachment ? t('Common.Uploading') : t('Common.Upload')}
                            helperText={t('Services.AttachmentUploadLimit', {
                                count: MAX_ATTACHMENT_FILES,
                                size: MAX_ATTACHMENT_TOTAL_SIZE_MB,
                            })}
                            className="mb-2"
                        />

                        {attachments.length > 0 ? (
                            <div className="flex flex-col gap-2">
                                {attachments.map((attachment) => (
                                    <div
                                        key={attachment.id}
                                        className="flex items-center justify-between gap-2 border border-border-default rounded-md px-3 py-2"
                                    >
                                        <a
                                            href={`${process.env.REACT_APP_IMG_URL || ''}${attachment.attachment}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-sm truncate text-primary-600"
                                        >
                                            {getAttachmentDisplayName(attachment)}
                                        </a>
                                        <RadixButton
                                            variant="danger"
                                            size="xs"
                                            className="!min-w-[70px]"
                                            onClick={() => confirmDeleteAttachment(attachment)}
                                            disabled={isDeletingAttachment}
                                        >
                                            {t('Common.Delete')}
                                        </RadixButton>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm m-0">{t('SpOffers.NoRes')}</p>
                        )}
                    </div>
                )}
            </div>

            {/* RIGHT SIDE */}
            <div className="w-full space-y-1 md:h-full md:overflow-y-auto md:pr-2 mt-7 md:mt-0 md:min-h-0 custom-scroll">
                <h4 className="m-0 font-normal">{t('POS.SelectService')}</h4>

                <RadixAccordionGroup type="multiple">
                    {services?.length > 0 ? (
                        services
                            ?.filter((group: any) => group?.services?.length > 0)
                            .map((group: any, idx: number) => {
                                const groupId = String(group?.id ?? idx);
                                const groupTitle = group?.group;
                                const servicesList: any[] = group?.services || [];

                                // ---- If no sub services
                                if (servicesList.length === 0) {
                                    const singleId = String(groupTitle);

                                    return (
                                        <RadixAccordion
                                            className="mb-2"
                                            asItem
                                            key={groupId}
                                            value={groupId}
                                            title={groupTitle}
                                        >
                                            <RadixCheckbox
                                                checked={formik?.values?.services?.includes(singleId)}
                                                onChange={(checked) => toggleService(singleId, checked)}
                                                label={groupTitle}
                                            />
                                        </RadixAccordion>
                                    );
                                }

                                // ---- Group with children
                                const groupIds = servicesList.map((svc) => String(svc.id ?? svc.value));

                                const allSelected =
                                    groupIds.length > 0 &&
                                    groupIds.every((id) => formik?.values?.services?.includes(id));

                                const someSelected = groupIds.some((id) => formik?.values?.services?.includes(id));

                                return (
                                    <RadixAccordion
                                        asItem
                                        key={groupId}
                                        value={groupId}
                                        className="mb-2"
                                        title={
                                            <div className="flex items-center gap-3 flex-1">
                                                <RadixCheckbox
                                                    checked={allSelected}
                                                    onChange={(checked) => toggleGroup(groupIds, checked)}
                                                    label={groupTitle}
                                                    className="flex-1"
                                                />
                                                {someSelected && !allSelected && (
                                                    <span className="text-xs text-gray-500">
                                                        (
                                                        {
                                                            groupIds.filter((id) =>
                                                                formik?.values?.services?.includes(id),
                                                            ).length
                                                        }
                                                        /{groupIds.length})
                                                    </span>
                                                )}
                                            </div>
                                        }
                                    >
                                        <div className="flex flex-col gap-3 ml-4">
                                            {servicesList.map((svc) => {
                                                const svcId = String(svc.id ?? svc.value);
                                                return (
                                                    <RadixCheckbox
                                                        key={svcId}
                                                        checked={formik?.values?.services?.includes(svcId)}
                                                        onChange={(checked) => toggleService(svcId, checked)}
                                                        label={svc?.name}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </RadixAccordion>
                                );
                            })
                    ) : (
                        <div className="text-gray-500 text-sm">{t('SpOffers.NoServ')}</div>
                    )}
                </RadixAccordionGroup>
                {formik?.touched?.services && formik?.errors?.services && (
                    <p className="text-red-500 text-sm mt-2">{formik?.errors?.services}</p>
                )}
            </div>

            {isPreview && (
                <RadixDialog
                    onOpenChange={() => setIsPreview(false)}
                    open={isPreview}
                    className="md:max-w-[80vw] h-[97%] flex flex-col pt-2"
                    title={
                        <h2 className="text-xl font-semibold m-0">
                            {t('Common.Email')} {t('Setting.Preview')}
                        </h2>
                    }
                >
                    <div className="flex-1 h-[95%]">
                        <iframe
                            title={t('Common.Email')}
                            srcDoc={generatePreviewHtml({ formik, defaultAdvanceReminderEmail, setting })}
                            width="100%"
                            height="100%"
                            className="w-full h-full"
                        />
                    </div>
                </RadixDialog>
            )}

            <RadixDialog
                overlayClassName="z-[9999]"
                contentClassName="z-[99999]"
                open={isDeleteAttachmentModalOpen}
                onOpenChange={(open) => {
                    if (isDeletingAttachment) return;
                    setIsDeleteAttachmentModalOpen(open);
                    if (!open) {
                        setAttachmentToDelete(null);
                    }
                }}
                title={t('Services.DeleteAttachmentTitle')}
                description={t('Services.DeleteAttachmentDescription')}
                footer={
                    <div className="flex items-center gap-2">
                        <RadixButton
                            variant="outline"
                            disabled={isDeletingAttachment}
                            onClick={() => {
                                setIsDeleteAttachmentModalOpen(false);
                                setAttachmentToDelete(null);
                            }}
                        >
                            {t('Setting.Cancel')}
                        </RadixButton>
                        <RadixButton variant="danger" onClick={handleDeleteAttachment} disabled={isDeletingAttachment}>
                            {t('Common.Delete')}
                        </RadixButton>
                    </div>
                }
            />
        </div>
    );
};

export default ReminderForm;
